import { DatePicker } from '@/components/basic/datepicker';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '@/components/shadcn/dialog';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from '@/components/shadcn/button';
import {
  AvailableTeamMembersEnum,
  OnboardingStatusEnum,
} from '@/__generated__/graphql';
import { useOrganizationContext } from '@/contexts/organization-context';
import { FragmentType, gql, useFragment } from '@/__generated__';
import { useToast } from '@/components/shadcn/use-toast';
import { logError } from '@/lib/rum';

const TeamMemberButtonFragment = gql(`
  fragment TeamMemberButtonFragment on TeamMember {
    status
  }
`);

const MutationAddTeamMember = gql(`
  mutation OrganizationAddTeamMember($input: AddTeamMemberInput!) {
    organization {
      teamMember {
        addTeamMember(input: $input) {
          ... on AddTeamMemberSuccess {
            result {
              name
              status
              start
              end
            }
          }
        }
      }
    }
  }
`);

const MutationOffBoardTeamMember = gql(`
  mutation OrganizationOffBoardTeamMember($input: OffBoardTeamMemberInput!) {
    organization {
      teamMember {
        offBoardTeamMember(input: $input) {
          ... on OffBoardTeamMemberSuccess {
            result {
              name
              status
              start
              end
            }
          }
        }
      }
    }
  }
`);

const MutationOnBoardTeamMember = gql(`
  mutation OrganizationOnBoardTeamMember($input: OnBoardTeamMemberInput!) {
    organization {
      teamMember {
        onBoardTeamMember(input: $input) {
          ... on OnBoardTeamMemberSuccess {
            result {
              name
              status
              start
              end
            }
          }
        }
      }
    }
  }
`);

export default function TeamMemberButton({
  teamMember,
  name,
}: {
  // teamMember: TeamMember | undefined;
  teamMember: FragmentType<typeof TeamMemberButtonFragment> | undefined;
  name: AvailableTeamMembersEnum;
}) {
  const teamMemberButtonFragment = useFragment(
    TeamMemberButtonFragment,
    teamMember
  );
  const [loading, setLoading] = useState(false);
  const isOnBoarded =
    teamMemberButtonFragment?.status === OnboardingStatusEnum.Done;
  const { organizationId } = useOrganizationContext();
  const { toast } = useToast();
  const [addTeamMember] = useMutation(MutationAddTeamMember, {
    refetchQueries: ['TeamMembersSettingsQuery'],
  });
  const [onBoardTeamMember] = useMutation(MutationOnBoardTeamMember, {
    refetchQueries: ['TeamMembersSettingsQuery'],
  });
  const [offBoardTeamMember] = useMutation(MutationOffBoardTeamMember, {
    refetchQueries: ['TeamMembersSettingsQuery'],
  });
  const [startDate, setStartDate] = useState(new Date());
  const buttonVariant =
    name === AvailableTeamMembersEnum.Maggie
      ? 'maggie-primary'
      : 'grant-primary';

  const handleOffBoardingTeamMember = async () => {
    setLoading(true);
    try {
      await offBoardTeamMember({
        variables: {
          input: {
            organizationId: organizationId || '',
            name,
            end: new Date(),
            disableWorkflow: true,
          },
        },
      });
    } catch (err) {
      logError(`Something went wrong while off-boarding ${name}`, err);
      toast({
        description: `Something went wrong while off-boarding ${name}`,
        duration: 3000,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOnBoardingTeamMember = async () => {
    setLoading(true);
    try {
      if (!teamMember) {
        await addTeamMember({
          variables: {
            input: {
              organizationId: organizationId || '',
              name,
            },
          },
        });
      }

      await onBoardTeamMember({
        variables: {
          input: {
            organizationId: organizationId as string,
            name,
            start: startDate,
            disableWorkflow: false,
          },
        },
      });
    } catch (err) {
      logError(`Something went wrong while on-boarding ${name}`, err);
      toast({
        description: `Something went wrong while on-boarding ${name}`,
        duration: 3000,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return isOnBoarded ? (
    <Dialog>
      <DialogTrigger asChild>
        <Button disabled={loading} variant={buttonVariant}>
          Disable {name}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <p className='text-lg'>Are you sure you want to disable {name}?</p>

        <div className='mt-4 flex justify-end gap-4'>
          <DialogClose asChild>
            <Button variant='destructive' onClick={handleOffBoardingTeamMember}>
              Continue
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant='ghost'>Cancel</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  ) : (
    <Dialog>
      <DialogTrigger asChild>
        <Button disabled={loading} variant={buttonVariant}>
          Enable {name}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[330px]'>
        <p className='text-lg'>Select Service Start Date</p>
        <DatePicker
          sendDate={new Date()}
          onChange={(date: Date | undefined) => {
            if (!date) return;
            // Create UTC date at midnight
            const utcDate = new Date(
              Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0
              )
            );
            setStartDate(utcDate);
          }}
        />
        <div className='flex justify-end gap-4'>
          <DialogClose asChild>
            <Button
              variant={buttonVariant}
              onClick={handleOnBoardingTeamMember}
            >
              Confirm
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
